import { Inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { ToastService } from 'mbs-ui-kit';
import { Company, NotificationPlanRule, NotifyToPlatform, PermissionsEnum } from 'mbs-ui-shared';
import Computer from 'mbs-ui-shared/src/lib/models/Computer';
import { NotifyToPlatformString } from 'mbs-ui-shared/src/lib/models/Notification';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import {
  CustomAlertingDefaultValues,
  CustomAlertingSettings,
  PorfileRecipientTypes,
  ProfilePostActions,
  RMMAlertTypes,
  TypeOfSetting
} from 'mbs-ui-shared@models/rmm-custom-notification-model';
import { RmmCustomAlertingService } from 'mbs-ui-shared@services/rmm-custom-alert.service';
import { RmmNotificationService } from 'mbs-ui-shared@services/rmm-notifications.service';
import { DateTimeUtils } from 'mbs-ui-shared@utils/DateTimeUtils';
import { AlertCustomisationHandler, RMM_ALERT_CUSTOMISATION_TOKEN } from 'mbs-ui-shared@utils/rmmAlertCustomisationHandler';
import { getTemperatureInFahrenheit } from 'mbs-ui-shared@utils/temperature';
import { duration } from 'moment';
import { BehaviorSubject, Observable, combineLatest, filter, finalize, map, noop, of, switchMap, take } from 'rxjs';

@UntilDestroy()
@Injectable()
export class RmmCustomAlertingServiceController {
  public usingGlobalSettings = false;
  public usingCompanySettings = false;
  public usingComputerSettings = false;
  public usingCustomSettings = false;

  public typeOfSetting: TypeOfSetting = TypeOfSetting.Custom;
  public settingsId: string;

  public globalSettings: Partial<CustomAlertingSettings>;
  public companySettings: Partial<CustomAlertingSettings>;
  public computerSettings: Partial<CustomAlertingSettings>;
  public customSettings: Partial<CustomAlertingSettings>;
  public profileSettingsName: string;
  public profilePostActions: BehaviorSubject<ProfilePostActions> = new BehaviorSubject(null);

  public customSettingsWereInheritedButNotSaved = false;

  public usedSettings = new BehaviorSubject<Partial<CustomAlertingSettings>>(null);
  public currentSettings: Partial<CustomAlertingSettings>;
  public readonly DefaultTemplate: any = { ...this.getAlertSettingsTemplate(), ...this.getRecipientSettingsTemplate() };

  public isLoading$ = new BehaviorSubject<boolean>(true);
  public providerId: string;
  public company: Company;
  public computer: Computer;
  public dataHasBeingChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private alertSettingsWithNumber = [
    `${RMMAlertTypes.HighCpuUsage}.alertPercent`,
    `${RMMAlertTypes.HighMemoryUsage}.alertPercent`,
    `${RMMAlertTypes.LowDiskSpace}.alertPercent`,
    `${RMMAlertTypes.HighTemperature}.alertHighTemperature`
  ];

  private alertSettingsWithArrays = [
    `${RMMAlertTypes.SoftwareChanges}.excludedNames`,
    `${RMMAlertTypes.SoftwareChanges}.excludedPNPClasses`,
    `${RMMAlertTypes.SoftwareChanges}.excludedVendor`,
    `${RMMAlertTypes.HardwareChanges}.excludedNames`,
    `${RMMAlertTypes.HardwareChanges}.excludedPNPClasses`,
    `${RMMAlertTypes.HardwareChanges}.excludedPNPDeviceIDs`,
    `${RMMAlertTypes.HardwareChanges}.excludedManufacturer`,
    `${RMMAlertTypes.LowDiskSpace}.excludedVolumeLabels`,
    `${RMMAlertTypes.LowDiskSpace}.excludedMountPoints`
  ];

  constructor(
    @Inject(RMM_ALERT_CUSTOMISATION_TOKEN) private rmmAlertCustomisationHandler: AlertCustomisationHandler,
    private computerFacade: ComputersFacade,
    private companyFacade: CompaniesFacade,
    private alertService: RmmCustomAlertingService,
    private profileService: RmmNotificationService,
    private authFacade: AuthFacade,
    private i18next: I18NextPipe,
    private ability: AbilityService,
    private toastService: ToastService
  ) {
    this.authFacade.currentUser$.pipe(take(1)).subscribe((user) => (this.providerId = user?.ProviderInfo?.Id));
    //  settings definition and setup
    this.isLoading$
      .pipe(
        filter((value) => !value),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        this.dataHasBeingChanged.next(false);

        // global settings usage
        if (this.typeOfSetting === TypeOfSetting.Global) {
          this.currentSettings = cloneDeep(
            this.globalSettings ? this.updateAlertSettingsValueIfNone(this.globalSettings) : this.DefaultTemplate
          );
          this.setUsingSettings(null);
        }

        // company settings usage
        if (this.typeOfSetting === TypeOfSetting.Company) {
          if (this.companySettings) {
            this.currentSettings = this.updateAlertSettingsValueIfNone(cloneDeep(this.companySettings));
            this.setUsingSettings(TypeOfSetting.Company);
          } else {
            this.currentSettings = cloneDeep(
              this.globalSettings ? this.updateAlertSettingsValueIfNone(this.globalSettings) : this.DefaultTemplate
            );
            this.setUsingSettings(TypeOfSetting.Global);
          }
        }

        // computer settings usage
        if (this.typeOfSetting === TypeOfSetting.Computer) {
          if (this.computerSettings) {
            this.currentSettings = this.updateAlertSettingsValueIfNone(cloneDeep(this.computerSettings));
            this.setUsingSettings(TypeOfSetting.Computer);
          } else if (this.customSettings) {
            this.currentSettings = this.updateAlertSettingsValueIfNone(cloneDeep(this.customSettings));
            this.setUsingSettings(TypeOfSetting.Custom);
          } else if (this.companySettings) {
            this.currentSettings = this.updateAlertSettingsValueIfNone(cloneDeep(this.companySettings));
            this.setUsingSettings(TypeOfSetting.Company);
          } else {
            this.currentSettings = cloneDeep(
              this.globalSettings ? this.updateAlertSettingsValueIfNone(this.globalSettings) : this.DefaultTemplate
            );
            this.setUsingSettings(TypeOfSetting.Global);
          }
        }

        if (this.currentSettings) this.makeSettingsAddon();
      });
  }

  private updateAlertSettingsValueIfNone(settings: Partial<CustomAlertingSettings>) {
    const updatedSettings = cloneDeep(settings);

    Object.keys(this.DefaultTemplate).forEach((key) => {
      if (!(key in updatedSettings)) updatedSettings[key] = cloneDeep(this.DefaultTemplate[key]);
    });

    Object.keys(updatedSettings).forEach((key) => {
      if (!updatedSettings[key]) return (updatedSettings[key] = cloneDeep(this.DefaultTemplate[key]));

      if (key in this.DefaultTemplate)
        Object.keys(this.DefaultTemplate[key]).forEach((subKey) => {
          if (!(subKey in updatedSettings[key])) updatedSettings[key][subKey] = cloneDeep(this.DefaultTemplate[key][subKey]);
        });
    });

    return updatedSettings;
  }

  public switchSettings(switchTo: TypeOfSetting) {
    switch (switchTo) {
      case TypeOfSetting.Computer:
        if (!this.computerSettings && this.usingGlobalSettings) {
          this.customSettingsWereInheritedButNotSaved = true;
          this.computerSettings = cloneDeep(this.globalSettings);
        }
        if (!this.computerSettings && this.usingCompanySettings) {
          this.customSettingsWereInheritedButNotSaved = true;
          this.computerSettings = cloneDeep(this.globalSettings);
        } else {
          this.currentSettings = cloneDeep(this.computerSettings);
        }
        break;
      case TypeOfSetting.Custom:
        this.currentSettings = cloneDeep(this.customSettings);
        break;
      case TypeOfSetting.Company:
        if (!this.companySettings && this.usingGlobalSettings) {
          this.companySettings = cloneDeep(this.globalSettings);
          this.customSettingsWereInheritedButNotSaved = true;
        } else {
          this.currentSettings = cloneDeep(this.companySettings);
        }
        break;
      case TypeOfSetting.Global:
        this.currentSettings = cloneDeep(this.globalSettings);
        break;
      default:
        this.currentSettings = cloneDeep(this.customSettings);
    }

    this.setUsingSettings(switchTo);
    this.makeSettingsAddon();
  }

  private setUsingSettings(typeOfSettings: TypeOfSetting) {
    this.usingGlobalSettings = typeOfSettings === TypeOfSetting.Global;
    this.usingCompanySettings = typeOfSettings === TypeOfSetting.Company;
    this.usingComputerSettings = typeOfSettings === TypeOfSetting.Computer;
    this.usingCustomSettings = typeOfSettings === TypeOfSetting.Custom;
  }

  public getCurrentSettingsTypeUsage(): TypeOfSetting {
    if (this.usingComputerSettings) return TypeOfSetting.Computer;
    if (this.usingCustomSettings) return TypeOfSetting.Custom;
    if (this.usingCompanySettings) return TypeOfSetting.Company;

    return TypeOfSetting.Global;
  }

  public setSettingsType(type: TypeOfSetting, id?: string) {
    this.clearSettings();
    this.typeOfSetting = type;
    this.settingsId = id;

    this.loadSettings();
  }

  public clearSettings() {
    this.company = null;
    this.computer = null;
    this.customSettingsWereInheritedButNotSaved = false;
    this.usingGlobalSettings = false;
    this.usingCompanySettings = false;
    this.usingComputerSettings = false;
    this.customSettings = null;
    this.currentSettings = null;
    this.typeOfSetting = TypeOfSetting.Custom;
    this.usedSettings.next(null);
  }

  private makeSettingsAddon() {
    // ConnectionLoss -> Notify to Platform checkbox has reversed value
    if (
      [NotifyToPlatform.All, NotifyToPlatform.ServersOnly, NotifyToPlatformString.All, NotifyToPlatformString.ServersOnly].includes(
        this.currentSettings?.connectionLoss?.notifyToPlatform
      )
    ) {
      const notifyToPlatform = this.currentSettings.connectionLoss?.notifyToPlatform;
      this.currentSettings = {
        ...this.currentSettings,

        connectionLoss: {
          ...this.currentSettings.connectionLoss,
          notifyToPlatform:
            this.currentSettings?.connectionLoss?.enabled &&
            [NotifyToPlatformString.ServersOnly, NotifyToPlatform.ServersOnly].includes(notifyToPlatform)
              ? NotifyToPlatform.All
              : NotifyToPlatform.ServersOnly
        }
      };
    }

    if (this.currentSettings?.recipient) {
      this.currentSettings = {
        ...this.currentSettings,
        recipient: {
          ...this.currentSettings.recipient,
          rule: this.currentSettings?.recipient?.type ?? NotificationPlanRule.None
        }
      };
    }

    this.usedSettings.next(cloneDeep(this.currentSettings));
  }

  private revertSettingsAddonBeforeSave() {
    this.currentSettings = this.getConnectionLossSettings(this.currentSettings);

    this.currentSettings = {
      ...this.currentSettings,
      recipient: {
        type: this.currentSettings?.recipient?.rule,
        emails: this.currentSettings?.recipient?.emails
      }
    };

    this.alertSettingsWithArrays.forEach((value) => {
      const [key, subKey] = value.split('.');
      this.currentSettings[key][subKey] = this.getFormArrayValueFromString(this.currentSettings[key][subKey]);
    });

    this.alertSettingsWithNumber.forEach((value) => {
      const [key, subKey] = value.split('.');
      this.currentSettings[key][subKey] = Number(this.currentSettings[key][subKey]);
    });
  }

  // Connection Loss -> Notify To Platform settings are reverting - should change it for payload
  public getConnectionLossSettings(settings: any) {
    if (settings?.connectionLoss?.notifyToPlatform !== undefined) {
      const notifyToPlatform = settings.connectionLoss?.notifyToPlatform;
      settings.connectionLoss.notifyToPlatform =
        settings?.connectionLoss?.enabled && notifyToPlatform ? NotifyToPlatform.ServersOnly : NotifyToPlatform.All;
    }

    return settings;
  }

  public getFormArrayValueFromString(values: any) {
    if (Array.isArray(values)) return values;
    if (!values || !values?.trim()) return [];
    return values
      .replaceAll('\r\n', ',')
      .replaceAll('\n', ',')
      .replaceAll('.', ',')
      .split(',')
      .flatMap((value) => (value.trim() === '' ? [] : value.trim()));
  }

  public getSaveObservable() {
    if (
      !this.currentSettings ||
      !this.ability.can('read', PermissionsEnum.Rmm) ||
      !this.ability.can('read', PermissionsEnum.ShowRmm) ||
      !this.ability.can('read', PermissionsEnum.Notification)
    )
      return of(false);

    this.revertSettingsAddonBeforeSave();

    let stream$: Observable<any>;

    if (
      this.typeOfSetting === TypeOfSetting.Global ||
      (this.typeOfSetting === TypeOfSetting.Company && this.usingCompanySettings) ||
      (this.typeOfSetting === TypeOfSetting.Computer && this.usingComputerSettings)
    ) {
      stream$ = this.alertService.saveSettings(this.typeOfSetting, this.currentSettings, this.settingsId);
    } else {
      this.customSettingsWereInheritedButNotSaved
        ? (stream$ = of(null))
        : (stream$ = this.alertService.deleteSettings(this.typeOfSetting, this.settingsId));
    }

    return stream$.pipe(
      switchMap(() => this.alertService.sendClearSettingCacheRequest()),
      switchMap(() => this.alertService.sendAgentSettingsCommand(TypeOfSetting.Global, null)),
      switchMap((value) => of(true)),
      untilDestroyed(this)
    );
  }

  public saveSettings(showToast = false) {
    this.getSaveObservable().subscribe({
      next: (result) => {
        if (result && showToast) this.toastService.success(this.i18next.transform('mbs-ui:rmm-custom-alerting.changesSaved'));
        this.dataHasBeingChanged.next(false);
      },
      error: noop
    });
  }

  // settings Load
  public loadSettings() {
    switch (this.typeOfSetting) {
      case TypeOfSetting.Company:
        this.settingsId ? this.fetchCompanySettingsUseCase(this.settingsId) : this.fetchGlobalSettings();
        break;
      case TypeOfSetting.Computer:
        this.settingsId ? this.fetchComputerSettingsUseCase(this.settingsId) : this.fetchGlobalSettings();
        break;
      case TypeOfSetting.Global:
        this.fetchGlobalSettings();
        break;
      case TypeOfSetting.Custom:
      default:
        this.isLoading$.next(false);
        break;
    }
  }

  private fetchGlobalSettings() {
    this.isLoading$.next(true);

    this.profileService
      .getProfileList({ profileType: TypeOfSetting.Global, include: 'notifications' })

      .pipe(
        switchMap((globalProfile) => {
          if (globalProfile.data.length && this.typeOfSetting === TypeOfSetting.Global) {
            return this.profileService.getProfileSettings(globalProfile?.data[0].id);
          }

          return of(null);
        }),
        untilDestroyed(this)
      )
      .subscribe((globalProfile) => {
        if (globalProfile) {
          this.globalSettings = globalProfile.notifications;
          this.customSettings = globalProfile.notifications;
          this.profilePostActions.next(globalProfile.postActions);
        } else {
          this.globalSettings = null;
        }

        if (this.typeOfSetting === TypeOfSetting.Global) {
          this.switchSettings(TypeOfSetting.Global);
          this.isLoading$.next(false);
        }
      });
  }

  private fetchCompanySettingsUseCase(id: string) {
    this.isLoading$.next(true);
    this.fetchGlobalSettings();

    if (id)
      combineLatest([
        this.companyFacade.getById$(id, true),
        this.profileService.getCompanyProfileList(id).pipe(map((profileList) => (profileList.length ? profileList[0] : null)))
      ])
        .pipe(untilDestroyed(this))
        .subscribe(([company, settings]) => {
          this.company = company;
          this.companySettings = settings?.notifications ?? null;
          if (settings) this.profilePostActions.next(settings.postActions);
          this.isLoading$.next(false);
        });
  }

  private fetchComputerSettingsUseCase(hid: string) {
    this.isLoading$.next(true);
    this.computerFacade.loadComputerByHid({ hid: hid });
    this.fetchGlobalSettings();

    this.computerFacade
      .getByHid(hid)
      .pipe(
        filter(Boolean),
        take(1),
        switchMap((computer) => {
          this.computer = computer;

          if (computer?.company?.id) {
            this.company = { ...computer?.company };

            return combineLatest([
              this.alertService.getSettings(TypeOfSetting.Computer, hid),
              this.profileService
                .getCompanyProfileList(this.company.id)
                .pipe(map((profileList) => (profileList.length ? profileList[0] : null))),
              this.profileService.getUsedProfileForComputer(this.providerId, hid)
            ]);
          }
          return combineLatest([this.alertService.getSettings(TypeOfSetting.Computer, hid), of(null)]);
        }),
        finalize(() => this.isLoading$.next(false)),
        untilDestroyed(this)
      )
      .subscribe(([computerSettings, companySettings, profileSettings]) => {
        this.profilePostActions.next(profileSettings?.postActions ?? companySettings?.postActions ?? null);
        this.companySettings = companySettings?.notifications ?? null;
        this.computerSettings = computerSettings?.body ? JSON.parse(computerSettings?.body) : null;

        this.profileSettingsName = profileSettings?.name;
        this.customSettings = profileSettings?.notifications
          ? {
              ...profileSettings.notifications,
              recipient: {
                type: NotificationPlanRule[profileSettings?.postActions?.general?.recipient?.type ?? PorfileRecipientTypes.None],
                emails: profileSettings?.postActions?.general?.recipient?.emails ?? []
              },
              pushNotification: {
                enabled: !!profileSettings?.postActions?.general?.pushNotification?.enabled
              },
              pushRecipient: {
                type: NotificationPlanRule[profileSettings?.postActions?.general?.pushRecipient?.type ?? PorfileRecipientTypes.None],
                users: profileSettings?.postActions?.general?.pushRecipient?.users ?? []
              }
            }
          : null;
      });
  }

  //  utility data for current info

  public getAlertPercent(fromType: RMMAlertTypes) {
    const defaultValue =
      fromType === RMMAlertTypes.HighTemperature
        ? CustomAlertingDefaultValues.alertPercent.eighty
        : CustomAlertingDefaultValues.alertPercent.ninety;
    if (!this.currentSettings || !(fromType in this.currentSettings)) return defaultValue;

    return (
      (this.currentSettings[fromType] as any)?.alertPercent ?? (this.currentSettings[fromType] as any)?.alertHighTemperature ?? defaultValue
    );
  }

  public getTimeDuration(fromType: RMMAlertTypes) {
    const defaultDuration =
      fromType === RMMAlertTypes.ConnectionLoss ? '00:20' : fromType === RMMAlertTypes.NoAntivirus ? '00:15' : '00:05';

    if (!this.currentSettings || !(fromType in this.currentSettings)) return defaultDuration;
    const settingsDuration =
      (this.currentSettings[fromType] as any)?.duration ?? (this.currentSettings[fromType] as any)?.alertWrongSystemTimeDifference;

    return settingsDuration
      ? DateTimeUtils.HumanizeDuration(duration(settingsDuration))
      : DateTimeUtils.HumanizeDuration(duration(defaultDuration));
  }

  public getFahrenheit(value: string): string {
    return getTemperatureInFahrenheit(value);
  }

  // default parameters per request

  public getAlertSettingsTemplate() {
    return {
      [RMMAlertTypes.HighCpuUsage]: this.getDefaultParamsByAlertType(RMMAlertTypes.HighCpuUsage),
      [RMMAlertTypes.HighMemoryUsage]: this.getDefaultParamsByAlertType(RMMAlertTypes.HighMemoryUsage),
      [RMMAlertTypes.LowDiskSpace]: this.getDefaultParamsByAlertType(RMMAlertTypes.LowDiskSpace),
      [RMMAlertTypes.NotUpdated]: this.getDefaultParamsByAlertType(RMMAlertTypes.NotUpdated),
      [RMMAlertTypes.SmartNotHealthy]: this.getDefaultParamsByAlertType(RMMAlertTypes.SmartNotHealthy),
      [RMMAlertTypes.SoftwareChanges]: this.getDefaultParamsByAlertType(RMMAlertTypes.SoftwareChanges),
      [RMMAlertTypes.HardwareChanges]: this.getDefaultParamsByAlertType(RMMAlertTypes.HardwareChanges),
      [RMMAlertTypes.ConnectionLoss]: this.getDefaultParamsByAlertType(RMMAlertTypes.ConnectionLoss),
      [RMMAlertTypes.NoAntivirus]: this.getDefaultParamsByAlertType(RMMAlertTypes.NoAntivirus),
      [RMMAlertTypes.SnmpDevice]: this.getDefaultParamsByAlertType(RMMAlertTypes.SnmpDevice),
      [RMMAlertTypes.ServiceState]: this.getDefaultParamsByAlertType(RMMAlertTypes.ServiceState),
      [RMMAlertTypes.HighTemperature]: this.getDefaultParamsByAlertType(RMMAlertTypes.HighTemperature),
      [RMMAlertTypes.WrongSystemTime]: this.getDefaultParamsByAlertType(RMMAlertTypes.WrongSystemTime)
    };
  }

  public getRecipientSettingsTemplate() {
    return {
      recipient: { rule: NotificationPlanRule.None, type: 0, emails: [] },
      pushRecipient: { type: NotificationPlanRule.None, users: [] },
      pushNotification: { enabled: false }
    };
  }

  public getDefaultParamsByAlertType(alertType: RMMAlertTypes) {
    switch (alertType) {
      case RMMAlertTypes.HighCpuUsage:
      case RMMAlertTypes.HighMemoryUsage:
        return {
          alertPercent: CustomAlertingDefaultValues.alertPercent.ninety,
          duration: CustomAlertingDefaultValues.duration.five,
          enabled: false
        };
      case RMMAlertTypes.LowDiskSpace:
        return {
          alertPercent: CustomAlertingDefaultValues.alertPercent.ninety,
          duration: CustomAlertingDefaultValues.duration.five,

          excludedVolumeLabels: [].join(', '),
          excludedMountPoints: [].join(', '),
          excludeBootVolume: false,
          excludeRemovableDisks: false,
          enabled: false
        };
      case RMMAlertTypes.NotUpdated:
      case RMMAlertTypes.SmartNotHealthy:
        return {
          duration: CustomAlertingDefaultValues.duration.five,
          enabled: false
        };
      case RMMAlertTypes.SoftwareChanges:
        return {
          excludedNames: [].join(', '),
          excludedVendor: [].join(', '),
          enabled: false
        };
      case RMMAlertTypes.HardwareChanges:
        return {
          excludedNames: CustomAlertingDefaultValues.hardwareChanges.excludedNames.join(', '),
          excludedPNPClasses: CustomAlertingDefaultValues.hardwareChanges.excludedPNPClasses.join(', '),
          excludedPNPDeviceIDs: CustomAlertingDefaultValues.hardwareChanges.excludedPNPDeviceIDs.join(', '),
          excludedManufacturer: [].join(', '),
          enabled: false
        };
      case RMMAlertTypes.ConnectionLoss:
        return {
          enabled: false,
          notifyToPlatform: NotifyToPlatform.ServersOnly,
          notifyOnRestore: false,
          duration: CustomAlertingDefaultValues.duration.twenty
        };
      case RMMAlertTypes.NoAntivirus:
        return {
          duration: CustomAlertingDefaultValues.duration.fifteen,
          enabled: false
        };
      case RMMAlertTypes.SnmpDevice:
        return {
          alerts: [],
          enabled: false
        };
      case RMMAlertTypes.ServiceState:
        return {
          duration: CustomAlertingDefaultValues.duration.five,
          enabled: false,
          includeServices: []
        };
      case RMMAlertTypes.HighTemperature:
        return {
          alertHighTemperature: CustomAlertingDefaultValues.alertPercent.eighty,
          duration: CustomAlertingDefaultValues.duration.five,
          enabled: false
        };
      case RMMAlertTypes.WrongSystemTime:
        return {
          alertWrongSystemTimeDifference: CustomAlertingDefaultValues.duration.five,
          enabled: false
        };
      default:
        return {};
    }
  }

  public syncCurrentData(data: CustomAlertingSettings) {
    this.dataHasBeingChanged.next(this.isChanged(data));
    this.currentSettings = cloneDeep(data);
  }

  private isChanged(data: Partial<CustomAlertingSettings>) {
    if (!this.currentSettings) return true;

    let isChanged = false;
    Object.keys(data).forEach((key) => {
      Object.keys(data[key]).forEach((subKey) => {
        if (data[key][subKey] !== this.currentSettings[key][subKey]) isChanged = true;
      });
    });

    return isChanged;
  }

  public updateRecipients(data: Partial<CustomAlertingSettings>) {
    this.dataHasBeingChanged.next(this.isChanged(data));

    this.usedSettings.next({
      ...cloneDeep(this.usedSettings.getValue()),
      recipient: {
        ...data.recipient,
        type: data.recipient.rule ?? NotificationPlanRule.None
      },
      pushRecipient: {
        type: data.pushRecipient.type ?? NotificationPlanRule.None,
        users: data.pushRecipient.users ?? []
      },
      pushNotification: { enabled: data.pushNotification.enabled }
    });
  }

  public openAlertCustomisation(): void {
    this.rmmAlertCustomisationHandler.navigate(this.typeOfSetting, this.settingsId);
  }

  public resetToDefaultValues() {
    this.dataHasBeingChanged.next(true);
    this.usedSettings.next(cloneDeep(this.DefaultTemplate));
  }

  public setupProfileSettings(settings: CustomAlertingSettings) {
    this.customSettings = this.updateAlertSettingsValueIfNone(settings);
    this.switchSettings(TypeOfSetting.Custom);
  }

  public loadCustom(profileId: string) {
    this.profileService
      .getProfileSettings(profileId)
      .pipe(take(1))
      .subscribe((profile) => {
        this.profilePostActions.next(profile.postActions);
        this.setupProfileSettings(profile.notifications);
      });
  }
}
